import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

export type InterpreterAiQcFilterValues = {
  startDate: string
  endDate: string
  aiQcStatus: string
  nameCheck: string
  languageCheck: string
  idCheck: string
  /** AI Noise Detection levels: HIGH / MEDIUM / LOW (multi-select). */
  noiseLevels: string[]
}

export type AiQcReportFilterValues = InterpreterAiQcFilterValues & {
  interpreterIds: string[]
  customerIds: number[]
}

export type InterpreterAiQcFilter = {
  startDate?: string
  endDate?: string
  aiQcStatus?: string
  nameCheck?: string
  languageCheck?: string
  idCheck?: string
  noiseLevels?: string[]
}

export type InterpreterAiQcSortField =
  | 'callid'
  | 'clientname'
  | 'aiqcstatus'
  | 'qcchecklist'
  | 'calldatetime'
  | 'ainoisedetection'

export type InterpreterAiQcLogRow = {
  callHeaderId?: number
  CallHeaderId?: number
  callId?: string
  CallId?: string
  contactId?: string
  ContactId?: string
  channel?: string
  Channel?: string
  isAiInterpretation?: boolean
  IsAiInterpretation?: boolean
  customerName?: string
  CustomerName?: string
  requesterName?: string
  RequesterName?: string
  nameResult?: string
  NameResult?: string
  languageResult?: string
  LanguageResult?: string
  agentIdResult?: string
  AgentIdResult?: string
  callDateTime?: string
  CallDateTime?: string
  aiQcStatusPass?: boolean
  AiQcStatusPass?: boolean
  nameMentioned?: boolean
  NameMentioned?: boolean
  languageMentioned?: boolean
  LanguageMentioned?: boolean
  idMentioned?: boolean
  IdMentioned?: boolean
  aiNoiseDetection?: string | null
  AiNoiseDetection?: string | null
}

export type InterpreterAiQcSummary = {
  overallComplianceScore?: number
  OverallComplianceScore?: number
  totalCallsAudited?: number
  TotalCallsAudited?: number
  passRate?: number
  PassRate?: number
}

export type InterpreterAiQcListResponse = {
  data?: InterpreterAiQcLogRow[]
  Data?: InterpreterAiQcLogRow[]
  summary?: InterpreterAiQcSummary
  Summary?: InterpreterAiQcSummary
  payload?: {pagination?: PaginationMeta}
  Payload?: {Pagination?: PaginationMeta}
}

type PaginationMeta = {
  total?: number
  Total?: number
  last_page?: number
  Last_page?: number
  from?: number
  From?: number
  to?: number
  To?: number
}

export const defaultInterpreterAiQcFilter = (): InterpreterAiQcFilterValues => ({
  startDate: '',
  endDate: '',
  aiQcStatus: '',
  nameCheck: '',
  languageCheck: '',
  idCheck: '',
  noiseLevels: [],
})

export const defaultAiQcReportFilter = (): AiQcReportFilterValues => ({
  ...defaultInterpreterAiQcFilter(),
  interpreterIds: [],
  customerIds: [],
})

export function buildInterpreterAiQcFilterPayload(
  values: InterpreterAiQcFilterValues
): InterpreterAiQcFilter {
  return {
    startDate: values.startDate ? new Date(values.startDate).toISOString() : undefined,
    endDate: values.endDate
      ? new Date(`${values.endDate}T23:59:59.999Z`).toISOString()
      : undefined,
    aiQcStatus: values.aiQcStatus || undefined,
    nameCheck: values.nameCheck || undefined,
    languageCheck: values.languageCheck || undefined,
    idCheck: values.idCheck || undefined,
    noiseLevels: values.noiseLevels?.length
      ? values.noiseLevels.map((v) => v.toUpperCase())
      : undefined,
  }
}

export function formatAiQcCallId(
  callHeaderId: number,
  channel?: string | null,
  isAiInterpretation?: boolean
): string {
  if (!callHeaderId) return '—'
  if (isAiInterpretation) return `AI-${String(callHeaderId).padStart(6, '0')}`
  const channelUpper = (channel ?? '').toUpperCase()
  let prefix = 'OPI-'
  if (channelUpper === 'VIDEO' || channelUpper === 'VRI') prefix = 'VRI-'
  else if (channelUpper === 'OSI' || channelUpper === 'INPERSON') prefix = 'OSI-'
  return `${prefix}${String(callHeaderId).padStart(6, '0')}`
}

export async function getInterpreterAiQcLogs(
  linguistId: string,
  filter: InterpreterAiQcFilter,
  page = 1,
  itemsPerPage = 10,
  search?: string,
  sort: InterpreterAiQcSortField = 'calldatetime',
  order: 'asc' | 'desc' = 'desc'
): Promise<InterpreterAiQcListResponse> {
  const resp = await axios.post(`${API_URL}/InterpreterAiQc/user/${linguistId}`, filter, {
    params: {
      page,
      items_per_page: itemsPerPage,
      order,
      sort,
      search: search?.trim() || undefined,
    },
  })
  return resp.data
}

export type AiQcReportRow = InterpreterAiQcLogRow & {
  callStatus?: string
  CallStatus?: string
  callDuration?: string
  CallDuration?: string
  callDate?: string
  CallDate?: string
  callTime?: string
  CallTime?: string
  accountName?: string
  AccountName?: string
  interpreterName?: string
  InterpreterName?: string
  interpreterUserId?: string
  InterpreterUserId?: string
  interpreterEmail?: string
  InterpreterEmail?: string
}

export type AiQcReportResponse = {
  data?: AiQcReportRow[]
  Data?: AiQcReportRow[]
  summary?: InterpreterAiQcSummary
  Summary?: InterpreterAiQcSummary
  payload?: {pagination?: PaginationMeta}
  Payload?: {Pagination?: PaginationMeta}
}

export function buildAiQcReportFilterPayload(values: AiQcReportFilterValues) {
  return {
    StartDate: values.startDate ? new Date(values.startDate).toISOString() : null,
    EndDate: values.endDate
      ? new Date(`${values.endDate}T23:59:59.999Z`).toISOString()
      : null,
    AiQcStatus: values.aiQcStatus || null,
    NameCheck: values.nameCheck || null,
    LanguageCheck: values.languageCheck || null,
    IdCheck: values.idCheck || null,
    InterpreterIds: values.interpreterIds?.length ? values.interpreterIds : null,
    CustomerIds: values.customerIds?.length ? values.customerIds : null,
    NoiseLevels: values.noiseLevels?.length ? values.noiseLevels.map((v) => v.toUpperCase()) : null,
  }
}

export async function getAiQcReport(
  filter: ReturnType<typeof buildAiQcReportFilterPayload>,
  page = 1,
  itemsPerPage = 10,
  search?: string,
  sort: InterpreterAiQcSortField = 'calldatetime',
  order: 'asc' | 'desc' = 'desc'
): Promise<AiQcReportResponse> {
  const resp = await axios.post(`${API_URL}/Reports/ai-qc-report`, filter, {
    params: {
      page,
      items_per_page: itemsPerPage,
      order,
      sort,
      search: search?.trim() || undefined,
    },
  })
  return resp.data
}

export function normalizeAiQcReportRow(row: AiQcReportRow) {
  const base = normalizeAiQcRow(row)
  return {
    ...base,
    callStatus: row.callStatus ?? row.CallStatus ?? '',
    callDuration: row.callDuration ?? row.CallDuration ?? '',
    callDate: row.callDate ?? row.CallDate ?? '',
    callTime: row.callTime ?? row.CallTime ?? '',
    accountName: row.accountName ?? row.AccountName ?? base.customerName,
    interpreterName: row.interpreterName ?? row.InterpreterName ?? '',
    interpreterUserId: row.interpreterUserId ?? row.InterpreterUserId ?? '',
    interpreterEmail: row.interpreterEmail ?? row.InterpreterEmail ?? '',
  }
}

export function normalizeAiQcRow(row: InterpreterAiQcLogRow) {
  const callHeaderId = row.callHeaderId ?? row.CallHeaderId ?? 0
  const channel = row.channel ?? row.Channel ?? ''
  const isAiInterpretation = row.isAiInterpretation ?? row.IsAiInterpretation ?? false
  const callId =
    row.callId ??
    row.CallId ??
    formatAiQcCallId(callHeaderId, channel, isAiInterpretation)

  return {
    callHeaderId,
    callId,
    contactId: row.contactId ?? row.ContactId ?? '',
    channel,
    isAiInterpretation,
    customerName: row.customerName ?? row.CustomerName ?? '',
    requesterName: row.requesterName ?? row.RequesterName ?? '',
    aiQcStatusPass: row.aiQcStatusPass ?? row.AiQcStatusPass ?? false,
    nameMentioned: row.nameMentioned ?? row.NameMentioned ?? false,
    languageMentioned: row.languageMentioned ?? row.LanguageMentioned ?? false,
    idMentioned: row.idMentioned ?? row.IdMentioned ?? false,
    callDateTime: row.callDateTime ?? row.CallDateTime ?? '',
    aiNoiseDetection: row.aiNoiseDetection ?? row.AiNoiseDetection ?? null,
  }
}

export function normalizeAiQcSummary(summary?: InterpreterAiQcSummary | null) {
  return {
    overallComplianceScore:
      summary?.overallComplianceScore ?? summary?.OverallComplianceScore ?? 0,
    totalCallsAudited: summary?.totalCallsAudited ?? summary?.TotalCallsAudited ?? 0,
    passRate: summary?.passRate ?? summary?.PassRate ?? 0,
  }
}

export function paginationFromResponse(resp: InterpreterAiQcListResponse) {
  const pag = resp.payload?.pagination ?? resp.Payload?.Pagination
  return {
    total: pag?.total ?? pag?.Total ?? 0,
    lastPage: pag?.last_page ?? pag?.Last_page ?? 1,
    from: pag?.from ?? pag?.From ?? 0,
    to: pag?.to ?? pag?.To ?? 0,
  }
}
